import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';

import { futeloCols } from '../components/configurations/futelo';
import {
  overallClubFemaleCols,
  overallClubMaleCols,
  overallNationalFemaleCols,
  overallNationalMaleCols,
} from '../components/configurations/overall';
import {
  seasonalClubFemaleCols,
  seasonalClubMaleCols,
  seasonalNationalCols,
} from '../components/configurations/seasonal';
import { tenYearsCols } from '../components/configurations/tenYears';

import { useCoefficients } from './coefficients';
import { useCoefficientsConfig } from './configuration';

import type { Coefficient } from '@shared-types/coefficient.types';
import { CoefficientRange } from '@shared-types/coefficient.types';

const configMapping: Record<string, ColDef[]> = {
  OVERALL_CLUB_FEMALE: overallClubFemaleCols,
  OVERALL_CLUB_MALE: overallClubMaleCols,
  OVERALL_ASSOCIATION_FEMALE: overallNationalFemaleCols,
  OVERALL_ASSOCIATION_MALE: overallNationalMaleCols,
  SEASONAL_CLUB_FEMALE: seasonalClubFemaleCols,
  SEASONAL_CLUB_MALE: seasonalClubMaleCols,
  SEASONAL_ASSOCIATION: seasonalNationalCols,
  TENYEARS: tenYearsCols,
  FUTELO: futeloCols,
};

export const useCoefficientsColumns = () => {
  const {
    configuration: { mode, competitionType, competitionSex },
  } = useCoefficientsConfig();
  const { coefficients } = useCoefficients();

  const config = useMemo(
    () =>
      configMapping[`${mode}_${competitionType}_${competitionSex}`] ??
      configMapping[`${mode}_${competitionType}`] ??
      configMapping[mode] ??
      [],
    [competitionSex, competitionType, mode],
  );

  const columns: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [];

    const overallRanking = coefficients?.[0]?.overallRanking;
    if (['TENYEARS', 'FUTELO', CoefficientRange.OVERALL].includes(mode) && overallRanking) {
      for (let year = overallRanking.baseSeasonYear; year <= overallRanking.targetSeasonYear; year++) {
        cols.push({
          field: `season-${year}`,
          headerValueGetter: () => `${(year - 1).toString().slice(-2)}/${year.toString().slice(-2)}`,
          headerTooltip:
            mode === 'TENYEARS' && year === overallRanking.targetSeasonYear ? 'ten_year_ranking_current_season' : '',
          valueGetter: ({ data: c }: ValueGetterParams<Coefficient>) =>
            c.seasonRankings?.find((sr) => sr.seasonYear === year)?.totalPoints,
          cellRenderer: ({ value }) => value?.toFixed(3) ?? '-',
          tooltipComponentParams: { replace: year.toString() },
        });
      }
    }
    cols.push(...config);

    return cols.filter(Boolean);
  }, [coefficients, config, mode]);

  return { columns };
};
